import React, { useState } from 'react';

import Box from '@mui/material/Box';
import { DataGrid, GridToolbarContainer, GridSlots } from '@mui/x-data-grid';
import { Dialog, DialogTitle, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DeviceWithSticks } from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import Button from '@mui/material/Button';
import SecurityUpdateIcon from '@mui/icons-material/SecurityUpdate';
import { updateActivationSticksBundleVersion } from '../api/updateActivationSticksBundleVersion';
import ToastWrapper from './ToastWrapper';
import { useToastMessage } from '../hooks/useToastMessage';
import { ToastType } from '../interfaces';
import { getActivationSticksBundleUpdateStatus } from '../api/getActivationSticksBundleUpdateStatus';
import { useAppDispatch } from '../store/hooks';
import { loadActivationSticks } from '../store/devices.reducer';

interface Props {
    open: boolean;
    device: DeviceWithSticks;
    onClose: () => void;
}

export const ActivationSticksDialog = ({ device, open, onClose }: Props) => {
    const dispatch = useAppDispatch();

    const sticks = device.activationSticks;
    const [stickUpdateStatus, setStickUpdateStatus] = useState<
        Record<string, { status: string; details: string }>
    >({});
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();
    const [isUpdating, setIsUpdating] = useState(false);

    const columns = [
        {
            field: 'activationStickId',
            headerName: transformKeyToTitle('activationStickId'),
            width: 200,
        },
        {
            field: 'systemVersion',
            headerName: transformKeyToTitle('systemVersion'),
            width: 150,
        },
        {
            field: 'updateStatus',
            headerName: 'Update Status',
            width: 150,
        },
        {
            field: 'updateDetails',
            headerName: 'Update Details',
            width: 300,
        },
    ];

    const rows = sticks.map((stick) => {
        const statusInfo = stickUpdateStatus[stick.activationStickId] || {
            status: stick.systemVersion === device.systemVersion ? 'up-to-date' : 'needs update',
            details: '',
        };

        return {
            ...stick,
            id: stick.activationStickId,
            systemVersion: statusInfo.status === 'completed' ? device.systemVersion : stick.systemVersion,
            updateStatus: statusInfo.status,
            updateDetails: statusInfo.details,
        };
    });

    const Toolbar = () => {
        const onUpdateActivationSticks = async () => {
            try {
                setIsUpdating(true);

                const initialStatusMap = device.activationSticks.reduce((acc, stick) => {
                    acc[stick.activationStickId] = {
                        status: 'running',
                        details: '',
                    };
                    return acc;
                }, {} as Record<string, { status: string; details: string }>);

                setStickUpdateStatus(initialStatusMap);

                await updateActivationSticksBundleVersion(device.deviceId!, device.systemVersion!);

                const pollStatus = async () => {
                    let attempts = 0;
                    const maxAttempts = 120; // timeout control (~6 minutes)

                    const intervalId = setInterval(async () => {
                        try {
                            const statusResponse = await getActivationSticksBundleUpdateStatus(
                                device.deviceId!,
                            );

                            if (statusResponse.status !== 'in progress') {
                                clearInterval(intervalId);
                                setIsUpdating(false);

                                const updatedStatusMap = statusResponse.sticksStatuses.reduce(
                                    (acc: any, stickStatus: any) => {
                                        acc[stickStatus.stickId] = {
                                            status: stickStatus.status,
                                            details: stickStatus.details,
                                        };
                                        return acc;
                                    },
                                    {} as Record<string, { status: string; details: string }>,
                                );

                                setStickUpdateStatus(updatedStatusMap);

                                dispatch(loadActivationSticks());

                                let message = statusResponse.details;

                                if (!message) {
                                    message =
                                        statusResponse.status === 'completed'
                                            ? 'Оновлено'
                                            : 'Не вдалося оновити';
                                }

                                setToastMessage({
                                    message: message,
                                    type:
                                        statusResponse.status === 'completed'
                                            ? ToastType.SUCCESS
                                            : ToastType.WARNING,
                                });
                            }

                            attempts++;

                            if (attempts >= maxAttempts) {
                                clearInterval(intervalId);
                                setIsUpdating(false);

                                const initialStatusMap = device.activationSticks.reduce((acc, stick) => {
                                    acc[stick.activationStickId] = {
                                        status: 'failed',
                                        details: 'timeout',
                                    };
                                    return acc;
                                }, {} as Record<string, { status: string; details: string }>);

                                setStickUpdateStatus(initialStatusMap);

                                setToastMessage({
                                    message: 'Оновлення триває занадто довго.',
                                    type: ToastType.WARNING,
                                });
                            }
                        } catch (err) {
                            clearInterval(intervalId);
                            setIsUpdating(false);
                            setToastMessage({
                                message: `Помилка перевірки статусу: ${err}`,
                                type: ToastType.WARNING,
                            });
                        }
                    }, 3000);
                };

                pollStatus();
            } catch (ex) {
                console.error(ex);
                setToastMessage({
                    message: `Під час оновлення флешок відбулася помилка: ${ex}`,
                    type: ToastType.WARNING,
                });
                setIsUpdating(false);
            }
        };

        return (
            <>
                <GridToolbarContainer>
                    <Button
                        startIcon={<SecurityUpdateIcon />}
                        color="primary"
                        disabled={isUpdating}
                        onClick={onUpdateActivationSticks}
                    >
                        Update Sticks
                        {isUpdating && (
                            <>
                                <span className="ml-2 inline-flex">
                                    <CircularProgress size={15} />
                                </span>
                            </>
                        )}
                    </Button>
                </GridToolbarContainer>
                <ToastWrapper
                    open={isOpened}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    autoHideDuration={15000}
                    onClose={handleCloseToast}
                    message={toastMessage?.message}
                    type={toastMessage?.type}
                />
            </>
        );
    };

    return (
        <Dialog onClose={onClose} open={open}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'right' }}>
                <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
            </DialogTitle>
            <Box sx={{ width: '600px' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    slots={{
                        toolbar: Toolbar as GridSlots['toolbar'],
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 5,
                            },
                        },
                    }}
                    pageSizeOptions={[5]}
                    disableRowSelectionOnClick
                    getRowClassName={(params) => {
                        switch (params.row.updateStatus) {
                            case 'needs update':
                                return 'row-needs-update';
                            case 'failed':
                                return 'row-failed';
                            case 'completed':
                                return 'row-completed';
                            case 'skipped':
                                return 'row-skipped';
                            default:
                                return '';
                        }
                    }}
                    sx={{
                        '& .row-needs-update': {
                            backgroundColor: 'rgba(255, 255, 0, 0.3)', // semi-transparent yellow
                        },
                        '& .row-failed': {
                            backgroundColor: 'rgba(255, 0, 0, 0.2)', // light red
                        },
                        '& .row-completed': {
                            backgroundColor: 'rgba(0, 255, 0, 0.2)', // light green
                        },
                        '& .row-skipped': {
                            backgroundColor: 'rgba(128, 128, 128, 0.2)', // light grey
                        },
                    }}
                />
            </Box>
        </Dialog>
    );
};
